import * as React from 'react';
import { Suspense } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { ExploreCategorySwitch } from '../../Components/ExploreCards/ExploreCategorySwitch';
import { Theme } from '../../Theme/Theme';
import { useTrans } from '../../Store/System/LocalizationStore';
import { useExploreCategoriesSuspense } from '../../Hooks/useExploreCategoriesSuspense';
import { InitialCategoriesSkeleton } from '../../Components/Skeletons/InitialCategoriesSkeleton';
import { ServiceLifecycleCard } from '../../Components/ServiceLifecycleBoundary';

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const AcademyScreenContainer = styled.div`
  animation: ${fadeIn} 0.5s ease-out;
  background: ${Theme.Colors.white};
`;

const HeroSection = styled.div`
  background: white;
  padding: 32px 0 24px 0;
  border-bottom: 1px solid ${Theme.Colors.lighterGrey};
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
`;

const HeroTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  margin: 0 0 8px 0;
  line-height: 1.3;
  color: ${Theme.Colors.textColor};

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const AccentText = styled.span`
  color: #00dda2;
`;

const HeroSubtitle = styled.p`
  font-size: 1rem;
  color: ${Theme.Colors.charcoal};
  margin: 0;
  line-height: 1.5;
  max-width: 600px;

  @media (max-width: 600px) {
    font-size: 0.9rem;
  }
`;

const CategoriesSection = styled.div`
  background: ${Theme.Colors.white};
  padding: 24px 0;
`;

export type AcademyScreenProps = {};

const AcademyCategories: React.FC = observer(() => {
  const { categories } = useExploreCategoriesSuspense('academy');

  return (
    <>
      {categories.map((category, index) => (
        <ExploreCategorySwitch
          key={category.id}
          category={category}
          categoryIndex={index}
          isLast={index === categories.length - 1}
          linkPrefix="/academy/"
        />
      ))}
    </>
  );
});

export const AcademyScreen: React.FC<AcademyScreenProps> = observer(() => {
  const { t } = useTrans();

  return (
    <AcademyScreenContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {t('academy.hero.titleStart', { defaultValue: 'Discover Your' })}{' '}
            <AccentText>{t('academy.hero.accent', { defaultValue: 'Wellness' })}</AccentText>{' '}
            {t('academy.hero.titleEnd', { defaultValue: 'Journey' })}
          </HeroTitle>
          <HeroSubtitle>
            {t('academy.hero.subtitle', {
              defaultValue: 'Webinars, articles, workouts, and more from our expert team.',
            })}
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ServiceLifecycleCard />

      <CategoriesSection>
        <Suspense fallback={<InitialCategoriesSkeleton />}>
          <AcademyCategories />
        </Suspense>
      </CategoriesSection>
    </AcademyScreenContainer>
  );
});
